import { apiClient } from "@/api";
import { ApiResponse, PaginatedData } from "@/types";
import { AxiosRequestConfig } from "axios";

export type IStatus = "active" | "pending";

export type IAgentStatus = "in_progress" | "under_review" | "completed" | "rejected" | "deleted";

export type IProjectStatus = "in_progress" | "under_review" | "completed" | "deleted";

export type IInquiryStatus = "in_progress" | "completed";

export interface GetListReq {
  search?: string;
  page: number;
  size: number;
  status?: IStatus;
}

export interface GetListUserManageRes {
  id: string;
  requester_name: string;
  email: string;
  company_name: string;
  status: IStatus;
  project_count: number;
  created_at: string;
}

export interface User {
  id: string;
  requester_name: string;
  email: string;
  phone: string;
  company_name: string;
  department: string;
  position: string;
  job_title: string;
  status: string;
  created_at: string;
}

export interface GetUserPrjReq {
  user_Id: string;
  page: number;
  page_size: number;
}

export interface UserPrjRes {
  id: string;
  name: string;
  description: string;
  status: IProjectStatus;
  created_at: string;
}

export interface UserAgentRes {
  id: string;
  name: string;
  short_description: string;
  status: IAgentStatus;
  selected_count: string;
  created_at: string;
}

export interface UserInquiryRes {
  id: string;
  title: string;
  status: IInquiryStatus;
  created_at: string;
}

export const UserManagementService = {
  getList: (params: GetListReq, config?: AxiosRequestConfig) => {
    return apiClient.get<ApiResponse<PaginatedData<GetListUserManageRes>>>("admin/users/", {
      ...config,
      params,
    });
  },
  getById: (userId: string, config?: AxiosRequestConfig) => {
    return apiClient.get<ApiResponse<User>>(`admin/users/${userId}`, config);
  },
  deleteUserById: (userId: string, config?: AxiosRequestConfig) => {
    return apiClient.delete(`admin/users/${userId}`, config);
  },
  getListUserProject: (params: GetUserPrjReq, config?: AxiosRequestConfig) => {
    const { user_Id, ...rest } = params;
    return apiClient.get<ApiResponse<PaginatedData<UserPrjRes>>>(
      `admin/users/${user_Id}/projects`,
      { ...config, params: rest },
    );
  },
  deleteUserProject: (userId: string, projectId: string, config?: AxiosRequestConfig) => {
    return apiClient.delete(`admin/users/${userId}/projects/${projectId}`, config);
  },
  getListUserAgent: (params: GetUserPrjReq, config?: AxiosRequestConfig) => {
    const { user_Id, ...rest } = params;
    return apiClient.get<ApiResponse<PaginatedData<UserAgentRes>>>(
      `admin/users/${user_Id}/agents`,
      { ...config, params: rest },
    );
  },
  deleteUserAgent: (userId: string, agentId: string, config?: AxiosRequestConfig) => {
    return apiClient.delete(`admin/users/${userId}/agents/${agentId}`, config);
  },
  getListUserInquiries: (params: GetUserPrjReq, config?: AxiosRequestConfig) => {
    const { user_Id, ...rest } = params;
    return apiClient.get<ApiResponse<PaginatedData<UserInquiryRes>>>(
      `admin/users/${user_Id}/inquiries`,
      { ...config, params: rest },
    );
  },
  deleteUserInquiry: (userId: string, inquiryId: string, config?: AxiosRequestConfig) => {
    return apiClient.delete(`admin/users/${userId}/inquiries/${inquiryId}`, config);
  },
  updateUserStatus: (userId: string, status: IStatus, config?: AxiosRequestConfig) => {
    return apiClient.patch<ApiResponse<{ id: string; status: string }>>(
      `admin/users/${userId}/status`,
      { status },
      config
    );
  },
};
